/**
 * MONSTER TOWN — THE COZY BED. HUD/AUDIO/TOUCH CREW (src/audio/**).
 *
 * "A little music that doesn't loop annoyingly" (brief, Art & audio).
 *
 * There is no loop. There is no sample. There is a chord pad that walks a small
 * progression, a sparse pentatonic melody that decides note-by-note whether to
 * play at all, a continuous filtered-noise breeze, and birds at random intervals.
 * A kid can leave this running for an hour and never hear the same bar twice,
 * and it costs a handful of oscillators.
 *
 * TRACKS ARE DATA. A new room mood is a new entry in `TRACKS`, never a change to
 * the scheduler below.
 */

import { makeRng, scheduleLayer, type Layer } from './synth'
import { PENTATONIC } from './cues'
import type { AudioBuses } from './engine'

export interface TrackDef {
  readonly id: string
  /** Root offsets (semitones from C4) the pad walks between. */
  readonly chords: readonly (readonly number[])[]
  /** Seconds a chord is held. */
  readonly chordSeconds: number
  /** Chance a melody slot actually sounds. Low = sparse and cozy. */
  readonly melodyDensity: number
  /** Seconds between melody slots. */
  readonly melodyStep: number
  /** Breeze/room-tone level. */
  readonly breeze: number
  /** Seconds between bird calls, min/max. 0 = no birds (indoors). */
  readonly birds: readonly [number, number]
  readonly padGain: number
  readonly melodyGain: number
}

function ch(...s: number[]): readonly number[] {
  return s
}

/** DATA. Add a mood here; never touch the scheduler. */
export const TRACKS: readonly TrackDef[] = [
  {
    id: 'town',
    chords: [ch(0, 4, 7), ch(-3, 0, 4), ch(5, 9, 12), ch(-5, 0, 4), ch(2, 5, 9)],
    chordSeconds: 7.5,
    melodyDensity: 0.42,
    melodyStep: 1.35,
    breeze: 0.05,
    birds: [7, 22],
    padGain: 0.075,
    melodyGain: 0.07,
  },
  {
    id: 'interior',
    chords: [ch(0, 4, 9), ch(-3, 2, 7), ch(5, 9, 14), ch(0, 5, 9)],
    chordSeconds: 9,
    melodyDensity: 0.3,
    melodyStep: 1.8,
    breeze: 0.022,
    birds: [0, 0],
    padGain: 0.08,
    melodyGain: 0.055,
  },
  {
    id: 'venue',
    chords: [ch(0, 4, 7), ch(2, 5, 9), ch(5, 9, 12), ch(7, 11, 14), ch(-3, 0, 4)],
    chordSeconds: 6,
    melodyDensity: 0.6,
    melodyStep: 1,
    breeze: 0.03,
    birds: [0, 0],
    padGain: 0.07,
    melodyGain: 0.075,
  },
  {
    id: 'creator',
    chords: [ch(0, 7, 12), ch(2, 9, 14), ch(-3, 4, 9)],
    chordSeconds: 8,
    melodyDensity: 0.5,
    melodyStep: 1.15,
    breeze: 0.02,
    birds: [0, 0],
    padGain: 0.08,
    melodyGain: 0.08,
  },
]

const BY_ID = new Map(TRACKS.map((t) => [t.id, t]))

const LOOKAHEAD = 1.8

function freqOf(semitone: number): number {
  return 261.6256 * Math.pow(2, semitone / 12)
}

export class MusicBed {
  private ctx: BaseAudioContext | null = null
  private buses: AudioBuses | null = null
  private track: TrackDef | null = null
  private rng = makeRng(0xc0ffee)

  private nextChordAt = 0
  private nextMelodyAt = 0
  private nextBirdAt = 0
  private chordIndex = 0

  private breezeSrc: AudioBufferSourceNode | null = null
  private breezeGain: GainNode | null = null

  seed(n: number): void {
    this.rng = makeRng(n)
  }

  get trackId(): string | null {
    return this.track?.id ?? null
  }

  attach(ctx: BaseAudioContext, buses: AudioBuses): void {
    this.ctx = ctx
    this.buses = buses
  }

  /** null stops the bed. Unknown ids are ignored rather than throwing. */
  setTrack(id: string | null): void {
    if (id === null) {
      this.track = null
      this.stopBreeze()
      return
    }
    const t = BY_ID.get(id)
    if (t === undefined) return
    if (this.track?.id === t.id) return
    this.track = t
    const now = this.ctx?.currentTime ?? 0
    // Restart the clocks so a track change is heard immediately, not in 7 seconds.
    this.nextChordAt = now + 0.1
    this.nextMelodyAt = now + 0.6
    this.nextBirdAt = now + 4 + this.rng() * 6
    this.startBreeze()
  }

  /**
   * Lookahead scheduler. Called from the engine's audio band every sim step; it
   * schedules anything due inside the next LOOKAHEAD seconds and returns.
   */
  update(): void {
    const ctx = this.ctx
    const buses = this.buses
    const t = this.track
    if (ctx === null || buses === null || t === null) return
    if (ctx.state !== 'running') return
    const now = ctx.currentTime
    const horizon = now + LOOKAHEAD

    while (this.nextChordAt < horizon) {
      this.scheduleChord(ctx, buses, t, Math.max(this.nextChordAt, now + 0.05))
      this.nextChordAt += t.chordSeconds
    }
    while (this.nextMelodyAt < horizon) {
      this.scheduleMelody(ctx, buses, t, Math.max(this.nextMelodyAt, now + 0.05))
      this.nextMelodyAt += t.melodyStep
    }
    const [bMin, bMax] = t.birds
    if (bMax > 0) {
      while (this.nextBirdAt < horizon) {
        this.scheduleBird(ctx, buses, Math.max(this.nextBirdAt, now + 0.05))
        this.nextBirdAt += bMin + this.rng() * Math.max(0.1, bMax - bMin)
      }
    }
    if (this.breezeGain !== null) {
      // Slow, irregular breathing on the room tone so it never sits still.
      const target = t.breeze * (0.6 + this.rng() * 0.7)
      this.breezeGain.gain.setTargetAtTime(target, now, 2.5)
    }
  }

  private scheduleChord(ctx: BaseAudioContext, buses: AudioBuses, t: TrackDef, at: number): void {
    // A random WALK, not a loop: never step to the same chord twice, and the
    // step size varies, so the progression has no audible period.
    const step = 1 + Math.floor(this.rng() * (t.chords.length - 1))
    this.chordIndex = (this.chordIndex + step) % t.chords.length
    const chord = t.chords[this.chordIndex]
    if (chord === undefined) return
    const hold = t.chordSeconds * 1.25
    for (const semi of chord) {
      const f = freqOf(semi - 12)
      const layer: Layer = {
        kind: 'tone',
        wave: 'triangle',
        freq: f,
        dur: hold,
        gain: t.padGain,
        attack: hold * 0.35,
        detune: 7,
        lowpass: 900,
      }
      scheduleLayer(ctx, buses.music, layer, at, this.rng)
    }
  }

  private scheduleMelody(ctx: BaseAudioContext, buses: AudioBuses, t: TrackDef, at: number): void {
    if (this.rng() > t.melodyDensity) return
    const idx = Math.floor(this.rng() * PENTATONIC.length)
    const f = PENTATONIC[idx] ?? PENTATONIC[0] ?? 440
    const long = this.rng() < 0.25
    const layer: Layer = {
      kind: 'tone',
      wave: 'sine',
      freq: f,
      dur: long ? 1.5 : 0.75,
      gain: t.melodyGain,
      attack: 0.08,
      lowpass: f * 4,
    }
    scheduleLayer(ctx, buses.music, layer, at, this.rng)
    // An occasional soft harmony a fifth up. Cheap, and it stops the melody from
    // sounding like a test tone.
    if (this.rng() < 0.3) {
      scheduleLayer(
        ctx,
        buses.music,
        { kind: 'tone', wave: 'sine', freq: f * 1.5, dur: 0.6, gain: t.melodyGain * 0.5, attack: 0.1, at: 0.12 },
        at,
        this.rng,
      )
    }
  }

  private scheduleBird(ctx: BaseAudioContext, buses: AudioBuses, at: number): void {
    const base = 1500 + this.rng() * 900
    scheduleLayer(
      ctx,
      buses.music,
      { kind: 'tone', wave: 'sine', freq: base, toFreq: base * 1.35, dur: 0.08, gain: 0.05, attack: 0.006 },
      at,
      this.rng,
    )
    if (this.rng() < 0.7) {
      scheduleLayer(
        ctx,
        buses.music,
        { kind: 'tone', wave: 'sine', freq: base * 1.2, toFreq: base * 0.9, dur: 0.1, gain: 0.045, attack: 0.006, at: 0.13 },
        at,
        this.rng,
      )
    }
  }

  // -- continuous room tone --------------------------------------------------

  private startBreeze(): void {
    const ctx = this.ctx
    const buses = this.buses
    if (ctx === null || buses === null || this.breezeSrc !== null) return
    const len = Math.floor(ctx.sampleRate * 3)
    const buf = ctx.createBuffer(1, len, ctx.sampleRate)
    const d = buf.getChannelData(0)
    let s = 0x2f6e2b1
    for (let i = 0; i < len; i++) {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0
      // Pink-ish: a cheap one-pole smoothing of white noise. Much kinder than
      // white on a small speaker.
      const white = s / 0x80000000 - 1
      d[i] = i === 0 ? white * 0.1 : (d[i - 1] ?? 0) * 0.86 + white * 0.14
    }
    const src = ctx.createBufferSource()
    src.buffer = buf
    src.loop = true
    const f = ctx.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.value = 700
    const g = ctx.createGain()
    g.gain.value = 0.0001
    src.connect(f)
    f.connect(g)
    g.connect(buses.music)
    src.start(ctx.currentTime + 0.05)
    this.breezeSrc = src
    this.breezeGain = g
  }

  private stopBreeze(): void {
    const g = this.breezeGain
    const s = this.breezeSrc
    const ctx = this.ctx
    if (g !== null && ctx !== null) g.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.4)
    if (s !== null) {
      try {
        s.stop((ctx?.currentTime ?? 0) + 1.5)
      } catch {
        /* already stopped */
      }
    }
    this.breezeSrc = null
    this.breezeGain = null
  }
}

export const music = new MusicBed()
